import {
    projectPhaseSearchType,
    projectSearchPlayerCount,
    projectSearchPlayerAge,
    playTimeSearchType,
    userCommunitySearchType,
    projectBenefitTypes,
    projectSearchSortType,
} from '@/Models';

import {
    enumToSelectOptions,
    mapEnumDisplayNameFromResource,
} from '@/Utils/EnumUtility';

import { fieldTypes } from '@/Types/FieldTypes';

import Resources from '@/Resources.js';
import InitialState from '@/InitialState';

export function buildProjectFilterSchemaList(
    searchCreatorPromise,
    tags,
    categories,
    isAuthenticated,
    isUserProfilePublic,
) {

    const hasFlatList = categories.every(c => c.subCategories.length === 0);
    const categoriesOptions = hasFlatList
        ? buildFlatListOfCategories(categories)
        : buildGroupedListOfCategories(categories);

    const tagsOptions = tags?.filter(c => c.isVisible)
        .reduce((acc, { name, projectTagID }) =>
            ({ ...acc, ...{ [name]: projectTagID } }), {});

    const areGameSpecificProjectPropertiesEnabled = InitialState.featureFlags.gameSpecificProjectPropertiesEnabled;

    return [
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'projectPhaseSearchTypes',
            label: Resources.Search.SearchProjectsFiltersStatusLabel,
            options: enumToSelectOptions(
                mapEnumDisplayNameFromResource(Resources.Search, projectPhaseSearchType),
                () => true,
                (a, b) => a.order - b.order,
            ),
            qa: 'ProjectStatuses',
            isEnabled: true,
        },
        {
            type: hasFlatList ? fieldTypes.checkboxGroup : fieldTypes.groupedCheckbox,
            modelName: 'projectCatalogCategories',
            label: Resources.Search.SearchProjectsFiltersCategoryLabel,
            options: categoriesOptions,
            qa: 'Categories',
            isEnabled: true,
        },
        {
            type: fieldTypes.multiCheckbox,
            modelName: 'projectTags',
            label: Resources.Search.SearchProjectsFiltersTagLabel,
            placeholder: Resources.Search.SearchProjectsFiltersTagPlaceholder,
            options: tagsOptions,
            noOptionCapitalize: true,
            renderTags: true, // enables display of removable tags below the filter
            qa: 'ProjectTags',
            isEnabled: true,
        },
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'userCommunitySearchTypes',
            label: Resources.Search.SearchProjectsFiltersCommunityLabel,
            options: enumToSelectOptions(mapEnumDisplayNameFromResource(Resources.Search, userCommunitySearchType)),
            visible: (model) =>
                isAuthenticated
                && isUserProfilePublic
                && (!!model.userCommunitySearchTypes || model.userCommunitySearchTypes === null),
            qa: 'Community',
            isEnabled: true,
        },
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'projectBenefits',
            label: Resources.Search.SearchProjectsFiltersBenefitLabel,
            options: enumToSelectOptions(mapEnumDisplayNameFromResource(Resources.Search, projectBenefitTypes)),
            qa: 'ProjectBenefits',
            isEnabled: true,
        },
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'playerAges',
            label: Resources.Search.SearchProjectsFiltersAgeLabel,
            options: enumToSelectOptions(mapEnumDisplayNameFromResource(Resources.Search, projectSearchPlayerAge)),
            qa: 'PlayerAges',
            isEnabled: areGameSpecificProjectPropertiesEnabled,
        },
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'playerCounts',
            label: Resources.Search.SearchProjectsFiltersPlayableLabel,
            options: enumToSelectOptions(mapEnumDisplayNameFromResource(Resources.Search, projectSearchPlayerCount)),
            qa: 'PlayerCounts',
            isEnabled: areGameSpecificProjectPropertiesEnabled,
        },
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'playTimes',
            label: Resources.Search.SearchProjectsFiltersPlaytimeLabel,
            options: enumToSelectOptions(mapEnumDisplayNameFromResource(Resources.Search, playTimeSearchType)),
            qa: 'PlayTimes',
            isEnabled: areGameSpecificProjectPropertiesEnabled,
        },
        {
            type: fieldTypes.autocomplete,
            modelName: 'creator',
            label: Resources.Search.SearchProjectsFiltersCreatorLabel,
            placeholder: Resources.Search.SearchProjectsFiltersCreatorPlaceholder,
            searchFn: searchCreatorPromise,
            valueComparer: (a, b) => a.name === b.name,
            minChars: 3,
            maxInputLength: 55,
            onInput: (val, model) => {
                if (!val) {
                    model.creatorName = null;
                    model.creatorID = null;
                    model.creator = null;
                }
            },
            qa: 'Creator',
            isEnabled: true,
        },
        {
            type: fieldTypes.text,
            modelName: 'term',
            label: Resources.Search.SearchProjectsFiltersSearchPhraseLabel,
            placeholder: Resources.Search.SearchProjectsFiltersSearchPhrasePlaceholder,
            rules: {
                max: 80,
            },
            qa: 'SearchTerm',
            isEnabled: true,
        },
    ].filter(f => f.isEnabled);
}

export function buildProjectSortSchemaList() {
    return [
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'sortType',
            label: Resources.Search.SearchProjectSortGroupLabel,
            options: enumToSelectOptions(
                mapEnumDisplayNameFromResource(Resources.Common, projectSearchSortType),
            ),
            props: {
                type: 'radio',
                showLabel: false,
            },
        },
    ];
}

function buildFlatListOfCategories(categories) {
    return Array.isArray(categories) ? categories
        .reduce((acc, { name, categoryID }) =>
            ({ ...acc, [name]: categoryID }), {})
        : [];
}

function buildGroupedListOfCategories(categories) {
    return Array.isArray(categories) ? categories
        .reduce((acc, { name, categoryID, subCategories }) => {
            if (subCategories && subCategories.length > 0) {
                acc.push({
                    name,
                    index: categoryID,
                    subOptions: subCategories.map(({ name, categoryID }) => ({
                        name,
                        value: categoryID,
                    })),
                });
            }
            return acc;
        }, [])
        : [];
}
